import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BooleanDisplay, Button, Divider, Loading, Panel, Typography, Modal } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useLazyGetOffloadingConfigurationQuery } from "@/redux/apis/addOn/addOnApi";
import { useAppSelector } from "@/redux/hooks";
import { addOnItemSelector } from "@/redux/slices/addOn/selectors";
import { OffloadingConfiguration } from "@/redux/slices/addOn/types";
import { RawErrorResponse } from "@/redux/types";
import { addToast } from "@/utils";
import { addOnsDisableMessage } from "../AddOns/fixtures";
import { useToggleAddOn } from "../AddOns/hooks/useToggleAddOn";
import { AddOnDisableErrorModal } from "../AddOns/modals/AddOnDisableErrorModal";
import { renderDispatchTime } from "./helpers";

export const OffloadingDetailsInDispatch = () => {
  const navigate = useNavigate();
  const offloading = useAppSelector(addOnItemSelector("offloading"));
  const configuration = offloading?.configuration as OffloadingConfiguration | undefined;
  const [getOffloadingConfiguration, { isFetching }] = useLazyGetOffloadingConfigurationQuery();
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showDisableErrorModal, setShowDisableErrorModal] = useState(false);

  const { toggleAddOn, isLoading: isToggling } = useToggleAddOn({
    onEnableSuccess: () => {
      addToast("success", "Offloading has been successfully enabled.");
      setShowEnableModal(false);
    },
    onDisableSuccess: () => {
      addToast("info", "Offloading has been disabled.");
      setShowDisableModal(false);
    },
    onEnableError: (e: RawErrorResponse) => {
      getErrorMessages(e).forEach((message) => addToast("danger", message));
      setShowEnableModal(false);
    },
    onDisableError: () => {
      setShowDisableModal(false);
      setShowDisableErrorModal(true);
    },
    shouldUpdateOperator: true,
  });

  useEffect(() => {
    if (offloading?.enabled) {
      getOffloadingConfiguration();
    }
  }, [offloading?.enabled, getOffloadingConfiguration]);

  const renderEnableModal = () => (
    <Modal open={showEnableModal} onClose={() => (isToggling ? null : setShowEnableModal(false))} className="w-[400px]">
      <div className="relative flex flex-col">
        {isToggling && <Loading />}
        <Typography variant="title">Turn on Offloading</Typography>
        <Typography variant="paragraph" className="mt-3">
          Offloading lets RideMinder automatically dispatch jobs to your private or public network when needed.
        </Typography>
        <Typography variant="paragraph" className="mt-3">
          Once enabled, offloading rules will apply to all eligible jobs moving forward.
        </Typography>
        <div className="mt-[22px] flex flex-row items-center justify-end gap-x-4">
          <Button variant="secondary" size="md" onClick={() => setShowEnableModal(false)} disabled={isToggling}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={() => toggleAddOn("offloading", true)} disabled={isToggling}>
            Turn On
          </Button>
        </div>
      </div>
    </Modal>
  );

  const renderDisableModal = () => (
    <Modal open={showDisableModal} onClose={() => (isToggling ? null : setShowDisableModal(false))} className="w-[400px]">
      <div className="relative flex flex-col">
        {isToggling && <Loading />}
        <Typography variant="title">Turn off Offloading</Typography>
        <Typography variant="paragraph" className="mt-3 whitespace-pre-wrap">
          {addOnsDisableMessage["offloading"]}
        </Typography>
        <Typography variant="paragraph" className="mt-3">
          This will immediately stop all offloading behavior.
        </Typography>
        <div className="mt-[22px] flex flex-row items-center justify-end gap-x-4">
          <Button variant="secondary" size="md" onClick={() => setShowDisableModal(false)} disabled={isToggling}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="bg-danger hover:bg-danger-dark"
            onClick={() => toggleAddOn("offloading", false)}
            disabled={isToggling}
          >
            Turn Off
          </Button>
        </div>
      </div>
    </Modal>
  );

  if (!offloading) return null;

  return (
    <>
      <Panel className="mt-5">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Typography variant="h3" className="leading-8">
              Offloading
            </Typography>
            <Typography className="text-neutral-dark-gray">
              Automatically dispatch jobs to your private or public network when needed.
            </Typography>
          </div>
          <div className="ml-auto flex gap-3">
            {offloading.enabled ? (
              <>
                <Button variant="secondary" startIcon="MinusCircle" iconClassName="text-danger" onClick={() => setShowDisableModal(true)}>
                  Turn Off
                </Button>
                <Button startIcon="Edit2" onClick={() => navigate("edit/offloading")}>
                  Edit
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={() => setShowEnableModal(true)}>
                Turn On
              </Button>
            )}
          </div>
        </div>

        <div className="relative my-4 flex w-full flex-col rounded-lg bg-neutral-surface-gray p-5">
          {isFetching && <Loading className="rounded-lg" />}
          <div className="flex flex-row items-center">
            <div className="flex flex-1 flex-col">
              <Typography variant="action">Auto Dispatch to Private Network</Typography>
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Set the time before pickup
              </Typography>
            </div>
            <BooleanDisplay status={offloading.enabled && configuration?.enabledAutomaticDispatchPrivateNetwork}>
              <Typography variant="paragraph">
                {offloading.enabled && configuration?.enabledAutomaticDispatchPrivateNetwork ? "Enabled" : "Disabled"}
              </Typography>
            </BooleanDisplay>
          </div>
          <div className="mt-5 flex flex-row items-center">
            <div className="flex flex-1 flex-row items-center gap-3">
              <Typography variant="action" className="text-neutral-dark-gray">
                Standard Hold Time
              </Typography>
            </div>
            <Typography variant="paragraph">
              {offloading.enabled
                ? renderDispatchTime(configuration?.dispatchAutoPrivateNetworkTime, configuration?.dispatchAutoPrivateNetworkTimeframe)
                : "-"}
            </Typography>
          </div>
          <Divider className="my-6" />
          <div className="flex flex-row items-center">
            <div className="flex flex-1 flex-col">
              <Typography variant="action">Auto Dispatch to Public Network</Typography>
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Set the time before pickup
              </Typography>
            </div>
            <BooleanDisplay status={offloading.enabled && configuration?.enabledAutomaticDispatchPublicNetwork}>
              <Typography variant="paragraph">
                {offloading.enabled && configuration?.enabledAutomaticDispatchPublicNetwork ? "Enabled" : "Disabled"}
              </Typography>
            </BooleanDisplay>
          </div>
          <div className="mt-5 flex flex-row items-center">
            <div className="flex flex-1 flex-row items-center gap-3">
              <Typography variant="action" className="text-neutral-dark-gray">
                Standard Hold Time
              </Typography>
            </div>
            <Typography variant="paragraph">
              {offloading.enabled
                ? renderDispatchTime(configuration?.dispatchAutoPublicNetworkTime, configuration?.dispatchAutoPublicNetworkTimeframe)
                : "-"}
            </Typography>
          </div>
        </div>
      </Panel>
      {renderEnableModal()}
      {renderDisableModal()}
      <AddOnDisableErrorModal open={showDisableErrorModal} onClose={() => setShowDisableErrorModal(false)} />
    </>
  );
};
